import React, { useEffect, useRef, useState } from 'react';
import GenericTableComponent from '../../genericComponents/tableComponent/GenericTableComponent';
import Icons from '../../assets/icons';
import { AlarmClock, Calendar, CircleCheck, Code, CornerDownRight, CornerUpLeft, FileClock, Repeat, SquareArrowOutUpRight, TriangleAlert } from 'lucide-react';
import { CONFIG } from '../../config';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { getPayloadFromURLParams, setPayloadAsURLParams } from '../../helper/URLParamsHelper';
import LanguageTranslator from '../../locals/LanguageTranslator';
import { Radio } from 'antd';
import GenericInput from '../../genericComponents/input/GenericInputComponent';
import { getMenuMode } from '../../helper/helperTableFunctions';

const DataAPIMonitor = (props) => {
    const [currentTab, setCurrentTab] = useState("INBOUND_OUTBOUND_API_LOGS");
    const generalStore = useSelector(state => state.generalStore);
    const [refresh, setRefresh] = useState(false);
    const [data, setData] = useState([])
    const [summaryData, setSummaryData] = useState({})
    const [dateRange, setDateRange] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [apiType, setApiType] = useState("ALL");
    const [updatetablePayload, setUpdatetablePayload] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleTableClick = (e) => {
            const refreshBtn = e.target.closest('.refresh-button');
            if (refreshBtn) {
                setSearchValue("");
                setApiType("ALL");
                setDateRange(null);
                setData([]);
                setSummaryData({});
                setPayloadAsURLParams({
                    data: { filter: {} },
                    h: props.history,
                    l: props.location
                });
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('click', handleTableClick);
        }
        return () => {
            if (container) {
                container.removeEventListener('click', handleTableClick);
            }
        };
    }, []);


    useEffect(() => {
        const params = getPayloadFromURLParams({ l: props.location });
        const existingFilter = params?.data?.filter?.creation_time || params?.filter?.creation_time;
        if (existingFilter?.from && existingFilter?.to) {
            setDateRange([dayjs(existingFilter.from), dayjs(existingFilter.to)]);
        }
    }, []);

    const filteredData = React.useMemo(() => {
        let res = data;
        if (apiType && apiType.toUpperCase() !== "ALL") {
            res = res.filter(item => item?.api_type?.toUpperCase() === apiType.toUpperCase());
        }
        if (searchValue) {
            const lowerSearch = searchValue.toLowerCase();
            res = res.filter(item => {
                const searchableText = [
                    item?.api_name,
                    item?.file_name,
                    item?.api_type
                ].join(" ").toLowerCase();
                return searchableText.includes(lowerSearch);
            });
        }

        return res;
    }, [data, searchValue, apiType]);


    const tableHeadersMap = {
        main: {
            name: "Main",
            key: currentTab == "INBOUND_OUTBOUND_API_LOGS" ? "INBOUND_OUTBOUND_API_LOGS" : "FILE_SYNC_STATUS_S3"
        }
    };
    const dateFormatter = (value) => {
        return dayjs(value).format("YYYY-MM-DD");
    }

    useEffect(() => {
        if (generalStore?.["getTableComponentListData"]?.isSuccess) {
            const summaryData = generalStore?.getTableComponentListData?.data?.summary || {};
            const data = generalStore?.getTableComponentListData?.data?.resource || [];
            setData(data)
            setSummaryData(summaryData)
        }
    }, [generalStore?.["getTableComponentListData"]?.data])

    const handleChange = (item, value) => {
        switch (item) {
            case "tabChange":
                setData([])
                setSummaryData({})
                setCurrentTab(value);
                setRefresh(false);
                setDateRange(null)
                setSearchValue("")
                setApiType("ALL")
                setPayloadAsURLParams({
                    data: { filter: {} },
                    h: props.history,
                    l: props.location
                })
                break;
            case "dateFilter":
                setDateRange(value)
                setUpdatetablePayload(true);
                setData([])
                setSummaryData({})
                break
            case "apiType":
                setApiType(value)
                break
            case "searchValue":
                setSearchValue(value);
                break;
        }
    };

    useEffect(() => {
        if (updatetablePayload) {
            setUpdatetablePayload(false);
        }
    }, [updatetablePayload])


    const handleRedirect = (e, apiName, apiType) => {
        e.stopPropagation();

        const isNewMenu = getMenuMode?.() === "new";
        const basePath = isNewMenu
            ? "/app/data/settings/logs/api"
            : "/administration/apilogs";

        const filter = { apiName };

        const encodedApiType = encodeURIComponent(JSON.stringify(apiType?.toUpperCase()))

        const encodedFilter = encodeURIComponent(JSON.stringify(filter))

        const queryParams = `?apiType=${encodedApiType}&filter=${encodedFilter}`;

        props.history.push(`${basePath}${queryParams}`);
    };



    const renderHeader = () => {
        return (
            <div className="flex items-center justify-between px-5 py-3 bg-[#FDFDFD] sticky top-0 z-[20] border-b border-gray-100 mt-[10px] mr-[15px] ml-[30px] rounded-[7px] shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center">
                        <Icons.FileCodeCorner size={16} />
                    </div>
                    <div className="text-[16px] font-semibold text[#21314B]">
                        <LanguageTranslator> Data Sync & API Monitoring</LanguageTranslator>
                    </div>
                </div>
                <div className='min-w-[500px] flex items-center'>
                    <Radio.Group className='generic-sectional-tabs !flex-1 flex' value={currentTab} onChange={(e) => handleChange("tabChange", e.target?.value || "INBOUND_OUTBOUND_API_LOGS")}>
                        <Radio.Button value={"FILE_SYNC_STATUS_S3"} className='!flex-1' ><LanguageTranslator>Data Sync Status S3</LanguageTranslator></Radio.Button>
                        <Radio.Button value={"INBOUND_OUTBOUND_API_LOGS"} className='!flex-1'><LanguageTranslator>API Monitoring Table</LanguageTranslator></Radio.Button>
                    </Radio.Group>
                </div>
            </div>
        );
    };

    const lhsExtras = () => {
        return (
            <div className='flex items-center gap-2'>
                <GenericInput
                    placeholder="Type To Search"
                    iconLeft={<Icons.GlobalSearchIcon />}
                    onChange={(e) => handleChange("searchValue", e?.target?.value || "")}
                    value={searchValue}
                    inputKey="searchValue"
                    genInputExtraClass="!max-w-[220px] !rounded-none !mb-0 !whitespace-normal !overflow-visible !text-clip"
                    dependentFieldsObject={["searchValue"]}
                    inputClassName="!ml-1 !whitespace-normal !text-clip !overflow-visible"
                    hideInputTooltip={true}
                />
                {currentTab === "INBOUND_OUTBOUND_API_LOGS" && <GenericInput
                    genInputExtraClass="!max-w-[150px] !mb-0"
                    key="selectView"
                    placeholder="API Type"
                    type="dropdown"
                    value={apiType}
                    onChange={(e) => handleChange("apiType", e?.label || "All")}
                    iconRight={<Icons.DownArrowNew />}
                    inputProps={() => ({
                        modalMainClass: '!top-[40px]',
                        headerKeys: {
                            "value": {},
                        },
                        itemKeys: {
                            "value": ["label"],
                        },
                        getListData: () => {
                            return [
                                { value: "ALL", label: "All" },
                                { value: "INBOUND", label: "Inbound" },
                                { value: "OUTBOUND", label: "Outbound" },
                            ]
                        }
                    })}
                />}
                <GenericInput
                    key="dateFilter"
                    type="range"
                    value={dateRange}
                    placeholder={["Date From", "Date To"]}
                    onChange={(datvalue) => handleChange("dateFilter", datvalue)}
                    genInputExtraClass="!max-w-[220px] !rounded-none !mb-0"
                    hideDefaultRanges={false}
                    dependentFieldsObject={[dateRange]}
                />
            </div>
        )
    }

    const paginationLeftExtras = () => {
        return (
            <div className='flex items-center gap-3'>
                <div className='min-w-[170px] flex items-center justify-between bg-[#FAFAFA] min-h-[44px] p-[10px] rounded-md'>
                    <div className='font-semibold text-[16px]'>{currentTab === "INBOUND_OUTBOUND_API_LOGS" ? summaryData?.total_api_calls || 0 : summaryData?.totalS3Sync || 0}</div>
                    <div className='flex gap-2 items-center'>
                        <div className='text-[11px]'>{currentTab === "INBOUND_OUTBOUND_API_LOGS" ? "Total APIs" : "Total Syncs"} </div>
                        <div className='bg-[#5D5FEF] p-2 min-w-[24px] min-h-[24px] rounded-md'>{currentTab === "INBOUND_OUTBOUND_API_LOGS" ? <Code size={20} color='white' /> : <Repeat size={20} color='white' />}</div>
                    </div>
                </div>
                <div className='min-w-[170px] flex items-center justify-between bg-[#FAFAFA] min-h-[44px] p-[10px] rounded-md'>
                    <div className='font-semibold text-[16px]'>{currentTab === "INBOUND_OUTBOUND_API_LOGS" ? summaryData?.failure_calls || 0 : summaryData?.failedS3Sync || 0}</div>
                    <div className='flex gap-2 items-center'>
                        <div className='text-[11px]'>{currentTab === "INBOUND_OUTBOUND_API_LOGS" ? "Failed APIs" : "Failed S3 Syncs"}</div>
                        <div className='bg-[#EF4444] p-2 min-w-[24px] min-h-[24px] rounded-md'><TriangleAlert size={20} color='white' /></div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div ref={containerRef} className="flex flex-col min-h-screen bg-[#FDFDFD] table-api-monitor">
                <GenericTableComponent
                    {...props}
                    tableDataPayloadUpdated={updatetablePayload}
                    resetstate={() => setUpdatetablePayload(false)}
                    key={currentTab}
                    apiName={(searchValue || (apiType && apiType.toUpperCase() !== "ALL") || data?.length > 0) ? "manualSearch" : undefined}
                    tableHeadersPayload={tableHeadersMap}
                    tableHeadersMap={tableHeadersMap}
                    getTableDataRequest="getTableComponentListRequest"
                    tableDataPayload={{
                        displayName: currentTab,
                        apiUrl: currentTab === "FILE_SYNC_STATUS_S3" ? "/admin/master/get/s3/file/sync/status" : "/admin/master/get/api/logs",
                        baseUrl: CONFIG.SM_BACKEND_BASE_URL,
                        pageNo: 1,
                        filter: {
                            ...(dateRange && dateRange[0] && dateRange[1] ? {
                                creation_time: {
                                    from: dateFormatter(dateRange[0]),
                                    to: dateFormatter(dateRange[1])
                                }
                            } : {}),
                        }
                    }}
                    refreshData={refresh}
                    tableComExtraHeaders={{
                        "X-Scope-Id": "administration.apilogs.view"
                    }}
                    hidePaginationDropdown={true}
                    getTableDataSuccess={
                        generalStore?.getTableComponentListData?.isLoading
                            ? false
                            : (searchValue || (apiType && apiType.toUpperCase() !== "ALL") || data?.length > 0)
                    }
                    getTableDataLoading={generalStore?.getTableComponentListData?.isLoading}
                    tableData={{
                        data: filteredData,
                        totalItems: filteredData.length,
                        currentPage: 1,
                        maxPage: 1,
                        getTableApiData: { resource: data }
                    }}
                    paginationLeftExtras={paginationLeftExtras}
                    actionBarConfig={{
                        search: false,
                        filters: false,
                        emailReport: false,
                        isSummaryApiRequire: true,
                        downloadReport: false,
                        topExtra: renderHeader,
                        lhsExtras: lhsExtras,
                    }}
                    braedCrumps={false}
                    tableConfig={{
                        columnSetting: { disabled: false },
                        customCols: {
                            api_name: (item) => (
                                currentTab === "INBOUND_OUTBOUND_API_LOGS" ? (
                                    <span
                                        className="text-[#111D31] hover:underline cursor-pointer text-[12px] flex items-center gap-1 "
                                        onClick={(e) => handleRedirect(e, item.api_name, item.api_type)}
                                    >
                                        {item.api_name}{<SquareArrowOutUpRight className="w-3.5 h-3.5" color="#2681ff" />}
                                    </span>
                                ) : item.api_name
                            ),
                            api_type: (item) => (
                                <div className="flex items-center gap-2">
                                    {item.api_type === "INBOUND" ? (
                                        <div className="flex items-center gap-1.5 text-[#1B9CFC] font-medium px-2 py-0.5 rounded text-xs">
                                            <CornerDownRight className="w-3.5 h-3.5" />
                                            <span>Inbound</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5 text-[#5D5FEF] font-medium px-2 py-0.5 rounded text-xs">
                                            <CornerUpLeft className="w-3.5 h-3.5" />
                                            <span>Outbound</span>
                                        </div>
                                    )}
                                </div>
                            ),
                            success_failed_combine_count: (item) => (
                                <div className="flex items-cente gap-3">
                                    {item.success_failed_combine_count
                                        ?.split(/\s+/)
                                        .map((part, index) => {
                                            const isSuccess = part.toUpperCase().startsWith("SUCCESS");
                                            const isFailed = part.toUpperCase().startsWith("FAILED");

                                            return (
                                                <span
                                                    key={index}
                                                    className={`px-2.5 py-1 text-[11px] font-bold uppercase rounded-md leading-none min-w-[100px] flex items-center justify-center
                                                    ${isSuccess
                                                            ? "bg-[#22C55E] text-white"
                                                            : isFailed
                                                                ? "bg-[#EF4444] text-white"
                                                                : ""
                                                        }
                                                `}
                                                >
                                                    {part}
                                                </span>
                                            );
                                        })}
                                </div>

                            ),
                            last_success_time: (item) => (
                                item?.last_success_time && <div className='flex items-center gap-1 text-[#03B675] !text-[12px]'>
                                    <CircleCheck size={13} color='#03B675' /> {item?.last_success_time}
                                </div>
                            ),
                            last_failure_time: (item) => (
                                item?.last_failure_time && <div className='flex items-center gap-1 text-[#FD3757] !text-[12px]'>
                                    <TriangleAlert size={13} color='#FD3757' /> {item?.last_failure_time}
                                </div>
                            ),
                            schedule_sync_frequency: (item) => (
                                <div className='flex items-center gap-1  !text-[12px]'>
                                    {item?.schedule_sync_frequency == "Hourly" && <AlarmClock size={13} color='#67768E' />}
                                    {item?.schedule_sync_frequency == "Daily" && <Calendar size={13} color='#67768E' />}
                                    {item?.schedule_sync_frequency == "Custom" && <FileClock size={13} color='#67768E' />}
                                    {item?.schedule_sync_frequency}
                                </div>
                            ),
                            last_status_received: (item) => (
                                <div className={`inline-block px-2.5 py-1 !text-[11px] uppercase rounded-md leading-none font-semibold ${item?.last_status_received === "PROCESSED" ? "bg-[#03B675] text-white" : item?.last_status_received === "RATE-LIMIT-EXCEED" ? "bg-[#FE9D0B] text-white" : "bg-[#FD3757] text-white"}`}>
                                    {item?.last_status_received}
                                </div>
                            )
                        }
                    }}
                    availableStores={["generalStore"]}
                />
            </div>
        </>
    );
};

export default DataAPIMonitor;